import { useSuspenseQuery } from '@apollo/client';
import React, { useMemo, useState } from 'react';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import {
  Badge,
  Button,
  ColDefBuilder,
  DataTable,
  Heading,
  Icon,
  Stack,
  Text,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useTenantIdFromParams } from '@eluve/session-helpers';

import { DisableBundleConfirmationDialog } from '../../template-bundles/DisableBundleConfirmationDialog';
import { TenantBundleDefaultsSection } from '../../template-bundles/TenantBundleDefaultsSection';
import {
  getTemplateBundleDisplayName,
  templateBundleListItemFragment,
} from '../../template-bundles/template-bundle.operations';
import { useDisableBundleFlow } from '../../template-bundles/useDisableBundleFlow';

import {
  MigrateDefaultBundleDialog,
  UpdateBundleBySpecialtyDialog,
  UpdateDoctorIdsDialog,
} from './bulk-default-bundle';
import {
  TenantTemplateBundleToggle,
  tenantTemplateBundleFragment,
} from './components/TenantTemplateBundleToggle';

const getTenantTemplateBundlesQuery = graphql(
  `
    query getTenantTemplateBundles($tenantId: uuid!) {
      tenantsByPk(id: $tenantId) {
        __typename
        id
        name
      }
      llmModelPromptTemplates(
        where: {
          isActive: { _eq: true }
          isBundle: { _eq: true }
          output_template: { tenantId: { _isNull: true } }
        }
        orderBy: { displayName: ASC }
      ) {
        ...TemplateBundleListItem
        tenant_template_bundles(where: { tenantId: { _eq: $tenantId } }) {
          ...TenantTemplateBundle
        }
      }
      ownBundles: llmModelPromptTemplates(
        where: {
          isBundle: { _eq: true }
          output_template: { tenantId: { _eq: $tenantId } }
        }
        orderBy: { displayName: ASC }
      ) {
        ...TemplateBundleListItem
        tenant_template_bundles(where: { tenantId: { _eq: $tenantId } }) {
          ...TenantTemplateBundle
        }
      }
      wildcardBundleDefaults: templateBundleDefaults(
        where: {
          specialty: { _isNull: true }
          language: { _isNull: true }
          _or: [
            { tenantId: { _eq: $tenantId } }
            { tenantId: { _isNull: true } }
          ]
        }
        orderBy: [{ createdAt: ASC }, { id: ASC }]
      ) {
        __typename
        id
        tenantId
        templateBundleId
      }
    }
  `,
  [tenantTemplateBundleFragment, templateBundleListItemFragment],
);

type EffectiveDefaultSource = 'rule' | 'global' | 'fallback';

const EFFECTIVE_DEFAULT_LABELS: Record<EffectiveDefaultSource, string> = {
  rule: 'Org default',
  global: 'Org default (global)',
  fallback: 'Org default (first enabled)',
};

const EFFECTIVE_DEFAULT_TITLES: Record<EffectiveDefaultSource, string> = {
  rule: 'Doctors without a personal or specialty-specific default get this bundle, set by the wildcard rule in the Bundle Defaults section below.',
  global:
    'Doctors without a personal or specialty-specific default get this bundle: the system-wide global default, which is enabled for this org. Add a wildcard rule in the Bundle Defaults section below to override it.',
  fallback:
    'No Bundle Defaults rule is set and the global default bundle is not enabled for this org, so the earliest-enabled bundle wins arbitrarily. Add a wildcard rule in the Bundle Defaults section below to set the default deliberately.',
};

type BundleRow = {
  llmModelPromptTemplateId: string;
  tenantId: string;
  promptTemplateName: string;
  outputTemplateName: string;
  modelName: string;
  isEnabled: boolean;
  updatedAt: string;
  displayName: string;
  effectiveDefaultSource: EffectiveDefaultSource | null;
};

const effectiveDefaultCell = (row: BundleRow) =>
  row.effectiveDefaultSource ? (
    <Badge
      variant={
        row.effectiveDefaultSource === 'fallback' ? 'warningSubtle' : 'success'
      }
      title={EFFECTIVE_DEFAULT_TITLES[row.effectiveDefaultSource]}
    >
      {EFFECTIVE_DEFAULT_LABELS[row.effectiveDefaultSource]}
    </Badge>
  ) : null;

const buildBundleColumns = (onRequestDisable: (row: BundleRow) => void) =>
  new ColDefBuilder<BundleRow>()
    .linkSortable(
      'displayName',
      (row) => `/admin/template-bundles/${row.llmModelPromptTemplateId}`,
      'Display Name',
    )
    .defaultSortable('promptTemplateName', 'Prompt Template')
    .defaultSortable('outputTemplateName', 'Output Template')
    .defaultSortable('modelName', 'Model')
    .defaultSortable('isEnabled', {
      label: 'Is Enabled for this Org',
      cellRenderer: (row) => {
        return (
          <TenantTemplateBundleToggle
            tenantId={row.tenantId}
            llmModelPromptTemplateId={row.llmModelPromptTemplateId}
            displayName={row.displayName}
            onRequestDisable={() => onRequestDisable(row)}
          />
        );
      },
    })
    .defaultSortable('effectiveDefaultSource', {
      label: 'Org Default',
      cellRenderer: effectiveDefaultCell,
    })
    .dateSortable('updatedAt', 'Updated At')
    .build();

// Columns for bundles OWNED by this org (created in the tenant editor). The
// name links to the tenant-scoped detail page rather than the global one.
const buildOwnBundleColumns = (
  onRequestDisable: (row: BundleRow) => void,
  tenantId: string,
) =>
  new ColDefBuilder<BundleRow>()
    .linkSortable(
      'displayName',
      (row) =>
        `/admin/organizations/${tenantId}/manage/template-bundles/${row.llmModelPromptTemplateId}`,
      'Display Name',
    )
    .defaultSortable('promptTemplateName', 'Prompt Template')
    .defaultSortable('outputTemplateName', 'Output Template')
    .defaultSortable('modelName', 'Model')
    .defaultSortable('isEnabled', {
      label: 'Is Enabled for this Org',
      cellRenderer: (row) => (
        <TenantTemplateBundleToggle
          tenantId={row.tenantId}
          llmModelPromptTemplateId={row.llmModelPromptTemplateId}
          displayName={row.displayName}
          onRequestDisable={() => onRequestDisable(row)}
        />
      ),
    })
    .defaultSortable('effectiveDefaultSource', {
      label: 'Org Default',
      cellRenderer: effectiveDefaultCell,
    })
    .dateSortable('updatedAt', 'Updated At')
    .build();

export const AdminTenantTemplateBundlesPage: React.FC = () => {
  const tenantId = useTenantIdFromParams()!;
  const [isMigrateBundleDialogOpen, setIsMigrateBundleDialogOpen] =
    useState(false);
  const [isUpdateBySpecialtyDialogOpen, setIsUpdateBySpecialtyDialogOpen] =
    useState(false);
  const [isUpdateDoctorIdsDialogOpen, setIsUpdateDoctorIdsDialogOpen] =
    useState(false);

  const { requestDisable, dialogProps } = useDisableBundleFlow({
    mode: 'tenant',
    tenantId,
  });

  const handleRequestDisable = useMemo(
    () => (row: BundleRow) => {
      requestDisable(row.llmModelPromptTemplateId, row.displayName);
    },
    [requestDisable],
  );

  const bundleColumns = useMemo(
    () => buildBundleColumns(handleRequestDisable),
    [handleRequestDisable],
  );

  const ownBundleColumns = useMemo(
    () => buildOwnBundleColumns(handleRequestDisable, tenantId),
    [handleRequestDisable, tenantId],
  );

  const {
    data: {
      llmModelPromptTemplates,
      tenantsByPk,
      ownBundles,
      wildcardBundleDefaults,
    },
  } = useSuspenseQuery(getTenantTemplateBundlesQuery, {
    variables: {
      tenantId,
    },
    context: eluveAdminHasuraContext,
  });

  const tenantName = tenantsByPk?.name ?? '';

  // Only offer bundles that are actually enabled for this tenant so the admin
  // can't assign a user a bundle they have no access to.
  const enabledBundles = useMemo(
    () =>
      llmModelPromptTemplates.filter(
        (b) => b.tenant_template_bundles?.[0]?.isEnabled,
      ),
    [llmModelPromptTemplates],
  );

  // The bundle a no-default doctor gets, mirroring the backend resolver's
  // tenant-level tiers (ENG-5236): wildcard tenant rule → global default if
  // enabled for this tenant → first enabled bundle by created_at.
  const effectiveDefault = useMemo(() => {
    const enabled = [...llmModelPromptTemplates, ...ownBundles]
      .map((bundle) => ({
        bundle,
        tenantBundle: bundle.tenant_template_bundles?.[0],
      }))
      .filter((entry) => entry.tenantBundle?.isEnabled);
    const enabledIds = new Set(enabled.map((entry) => entry.bundle.id));

    // wildcardBundleDefaults is ordered createdAt/id ASC, matching the
    // resolver's tiebreak for duplicate wildcard rules.
    const tenantRule = wildcardBundleDefaults.find(
      (d) => d.tenantId === tenantId && enabledIds.has(d.templateBundleId),
    );
    if (tenantRule) {
      return {
        bundleId: tenantRule.templateBundleId,
        source: 'rule' as EffectiveDefaultSource,
      };
    }

    const globalDefault = wildcardBundleDefaults.find(
      (d) => d.tenantId === null,
    );
    if (globalDefault && enabledIds.has(globalDefault.templateBundleId)) {
      return {
        bundleId: globalDefault.templateBundleId,
        source: 'global' as EffectiveDefaultSource,
      };
    }

    const firstEnabled = [...enabled].sort(
      (a, b) =>
        (a.tenantBundle?.createdAt ?? '').localeCompare(
          b.tenantBundle?.createdAt ?? '',
        ) || a.bundle.id.localeCompare(b.bundle.id),
    )[0];
    if (firstEnabled) {
      return {
        bundleId: firstEnabled.bundle.id,
        source: 'fallback' as EffectiveDefaultSource,
      };
    }

    return null;
  }, [llmModelPromptTemplates, ownBundles, wildcardBundleDefaults, tenantId]);

  const toBundleRow = useMemo(
    () =>
      (bundle: (typeof llmModelPromptTemplates)[number]): BundleRow => {
        const promptName = bundle.template?.name ?? 'Unknown Prompt';
        const outputName = bundle.output_template?.name ?? 'None';
        const modelName =
          bundle.model_args?.name ??
          bundle.model_args?.modelType ??
          'Unknown Model';
        const tenantBundle = bundle.tenant_template_bundles?.[0];

        return {
          llmModelPromptTemplateId: bundle.id,
          tenantId,
          promptTemplateName: promptName,
          outputTemplateName: outputName,
          modelName,
          isEnabled: tenantBundle?.isEnabled ?? false,
          updatedAt: tenantBundle?.updatedAt ?? '',
          displayName: getTemplateBundleDisplayName(bundle),
          effectiveDefaultSource:
            effectiveDefault?.bundleId === bundle.id
              ? effectiveDefault.source
              : null,
        };
      },
    [tenantId, effectiveDefault],
  );

  const rows = useMemo(
    () => llmModelPromptTemplates.map(toBundleRow),
    [llmModelPromptTemplates, toBundleRow],
  );

  const ownRows = useMemo(
    () => ownBundles.map(toBundleRow),
    [ownBundles, toBundleRow],
  );

  return (
    <Stack className="grid" gap={2}>
      <Stack align="start" gap={2} direction="horizontal" justify="between">
        <Stack gap={2}>
          <Heading size="2xl">Template Bundles</Heading>
          <Text>
            Control which template bundles (prompt + output template + model)
            this organization will have access to
          </Text>
        </Stack>
        <Stack direction="horizontal" gap={2}>
          <Button
            variant="outline"
            onClick={() => setIsMigrateBundleDialogOpen(true)}
          >
            <Icon name="ArrowRightLeft" size="auto" />
            Migrate Default Bundle
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsUpdateBySpecialtyDialogOpen(true)}
          >
            <Icon name="Stethoscope" size="auto" />
            Update by Specialty
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsUpdateDoctorIdsDialogOpen(true)}
          >
            <Icon name="List" size="auto" />
            Update Doctor IDs
          </Button>
        </Stack>
      </Stack>
      <DataTable
        data={rows}
        columns={bundleColumns}
        isPaginated={false}
        enableGlobalSearch
        initialSortingState={[
          {
            id: 'isEnabled',
            desc: true,
          },
        ]}
      />

      <DisableBundleConfirmationDialog {...dialogProps} />

      <MigrateDefaultBundleDialog
        tenantId={tenantId}
        tenantName={tenantName}
        bundles={enabledBundles}
        open={isMigrateBundleDialogOpen}
        onOpenChange={setIsMigrateBundleDialogOpen}
      />

      <UpdateBundleBySpecialtyDialog
        tenantId={tenantId}
        tenantName={tenantName}
        bundles={enabledBundles}
        open={isUpdateBySpecialtyDialogOpen}
        onOpenChange={setIsUpdateBySpecialtyDialogOpen}
      />

      <UpdateDoctorIdsDialog
        tenantId={tenantId}
        tenantName={tenantName}
        bundles={enabledBundles}
        open={isUpdateDoctorIdsDialogOpen}
        onOpenChange={setIsUpdateDoctorIdsDialogOpen}
      />

      <div className="mt-8">
        <Stack gap={2}>
          <Heading size="xl">This organization's templates</Heading>
          <Text>
            Template bundles this organization created in the template editor.
            They're owned by the organization — open one to view its versions
            and model.
          </Text>
          {ownRows.length === 0 ? (
            <Text color="tertiary">
              No organization-specific templates yet.
            </Text>
          ) : (
            <DataTable
              data={ownRows}
              columns={ownBundleColumns}
              isPaginated={false}
              enableGlobalSearch
            />
          )}
        </Stack>
      </div>

      <div className="mt-8">
        <TenantBundleDefaultsSection
          tenantId={tenantId}
          tenantName={tenantName}
          enabledBundles={enabledBundles}
        />
      </div>
    </Stack>
  );
};
